/**
 * Audio Manager
 *
 * Handles loading, decoding, and playback of audio files
 */

export class AudioManager {
  private audioContext: AudioContext | null = null;
  private buffers: Map<string, AudioBuffer> = new Map();
  private currentSource: AudioBufferSourceNode | null = null;
  private gainNode: GainNode | null = null;
  private startedAt: number = 0;
  private pausedAt: number = 0;
  private isPaused: boolean = false;

  constructor() {
    this.initAudioContext();
  }

  private initAudioContext(): void {
    try {
      this.audioContext = new AudioContext();
      this.gainNode = this.audioContext.createGain();
      this.gainNode.connect(this.audioContext.destination);
      console.log('AudioManager initialized');
    } catch (error) {
      console.error('Failed to initialize AudioContext:', error);
    }
  }

  /**
   * Load an audio file from URL
   */
  async loadAudio(id: string, url: string): Promise<void> {
    if (!this.audioContext) {
      throw new Error('AudioContext not initialized');
    }

    try {
      console.log(`Loading audio: ${id} from ${url}`);
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);

      this.buffers.set(id, audioBuffer);
      console.log(`Audio loaded successfully: ${id}`);
    } catch (error) {
      console.error(`Failed to load audio ${id}:`, error);
      throw error;
    }
  }

  /**
   * Play a loaded audio buffer
   */
  play(id: string, startTime: number = 0): void {
    if (!this.audioContext || !this.gainNode) {
      console.error('AudioContext not initialized');
      return;
    }

    const buffer = this.buffers.get(id);
    if (!buffer) {
      console.error(`Audio buffer not found: ${id}`);
      return;
    }

    // Stop current playback if any
    this.stop();

    // Create new source node
    this.currentSource = this.audioContext.createBufferSource();
    this.currentSource.buffer = buffer;
    this.currentSource.connect(this.gainNode);

    // Start playback
    this.currentSource.start(this.audioContext.currentTime, startTime);
    this.startedAt = this.audioContext.currentTime - startTime;
    this.isPaused = false;
    console.log(`Playing audio: ${id} from ${startTime}s`);
  }

  /**
   * Pause current playback
   */
  pause(): number {
    if (!this.audioContext || !this.currentSource || this.isPaused) {
      return 0;
    }

    this.pausedAt = this.audioContext.currentTime - this.startedAt;
    this.stop();
    this.isPaused = true;
    console.log(`Audio paused at ${this.pausedAt}s`);
    return this.pausedAt;
  }

  /**
   * Resume from paused position
   */
  resume(id: string): void {
    if (!this.isPaused) return;

    this.play(id, this.pausedAt);
    this.isPaused = false;
  }

  /**
   * Get current playback position
   */
  getCurrentPosition(): number {
    if (!this.audioContext) return 0;
    if (this.isPaused) return this.pausedAt;
    return this.audioContext.currentTime - this.startedAt;
  }

  /**
   * Stop current playback
   */
  stop(): void {
    if (this.currentSource) {
      try {
        this.currentSource.stop();
      } catch (error) {
        // Source may already be stopped
      }
      this.currentSource.disconnect();
      this.currentSource = null;
    }
  }

  /**
   * Set volume (0.0 to 1.0)
   */
  setVolume(volume: number): void {
    if (this.gainNode) {
      this.gainNode.gain.value = Math.max(0, Math.min(1, volume));
    }
  }

  /**
   * Get audio context
   */
  getAudioContext(): AudioContext | null {
    return this.audioContext;
  }

  /**
   * Check if audio is loaded
   */
  isLoaded(id: string): boolean {
    return this.buffers.has(id);
  }

  /**
   * Get duration of loaded audio (in seconds)
   */
  getDuration(id: string): number {
    const buffer = this.buffers.get(id);
    return buffer ? buffer.duration : 0;
  }

  /**
   * Cleanup resources
   */
  dispose(): void {
    this.stop();
    this.buffers.clear();

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }
}
